/* import __COLOCATED_TEMPLATE__ from './row-actions.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'ember-intl/services/intl';
import type AppService from 'embercom/services/app-service';
import type Store from '@ember-data/store';
import type { KnowledgeHubTableRow } from 'embercom/models/content-service/knowledge-hub-content-wrapper';
import { EntityType } from 'embercom/models/data/entity-types';
import { taskFor } from 'ember-concurrency-ts';
import { dropTask } from 'ember-concurrency-decorators';
import type { TaskGenerator } from 'ember-concurrency';
import { SyncStatus } from 'embercom/models/knowledge-hub/sync-source-wrapper';

interface Args {
  tableRow: KnowledgeHubTableRow;
  activeHoverRowId?: string | null;
}

export default class RowActions extends Component<Args> {
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare appService: AppService;
  @service declare notificationsService: any;
  @tracked isDropdownOpen = false;

  get isSyncableContent(): boolean {
    let tableRow = this.args.tableRow;
    let entityType = 'entityType' in tableRow ? tableRow.entityType : tableRow.contentType;
    return (
      entityType === EntityType.ExternalContent || entityType === EntityType.ContentImportRunRecord
    );
  }

  get shouldShowActions(): boolean {
    // Only show actions for syncable content when hovering over this specific row OR when dropdown is open
    return (
      this.isSyncableContent &&
      (this.args.activeHoverRowId === this.args.tableRow.id || this.isDropdownOpen)
    );
  }

  private getFolder() {
    let tableRow = this.args.tableRow;
    let folderId = (tableRow as any).relations?.folder?.folder_id;
    if (!folderId) {
      return null;
    }
    return this.store.peekRecord('content-service/folder', folderId);
  }

  private getSyncSource(folder: any) {
    if (!folder) {
      return null;
    }
    let syncSources = this.store.peekAll('knowledge-hub/sync-source-wrapper');
    return syncSources.find((source) => {
      return source.folderId && source.folderId.toString() === folder.id.toString();
    });
  }

  get isSyncing(): boolean {
    let folder = this.getFolder();
    let syncSource = this.getSyncSource(folder);
    return syncSource?.isSyncing || taskFor(this.resyncTask).isRunning;
  }

  get actionOptions() {
    return [
      {
        items: [
          {
            text: this.intl.t('knowledge-hub.filterable-list.row-actions.resync'),
            icon: 'sync',
            isDisabled: this.isSyncing,
            tooltipText: this.isSyncing
              ? this.intl.t(
                  'knowledge-hub.overview.sections.syncs.rows.active.action.options.disabled-resync-tooltip',
                )
              : undefined,
            tooltip: { isDelayed: false },
            onSelectItem: () => {
              if (!this.isSyncing) {
                taskFor(this.resyncTask).perform();
              }
            },
          },
          {
            text: this.intl.t('knowledge-hub.filterable-list.row-actions.exclude-from-sync'),
            icon: 'delete',
            isDisabled: true,
          },
        ],
      },
    ];
  }

  @action
  handleDropdownOpen(): void {
    this.isDropdownOpen = true;
  }

  @action
  handleDropdownClose(): void {
    this.isDropdownOpen = false;
  }

  @dropTask
  *resyncTask(): TaskGenerator<void> {
    let tableRow = this.args.tableRow;

    try {
      // Expected format: "{app_id}_{content_wrapper_id}" (e.g., "8_165_15417" -> "165_15417")
      let fullId = String(tableRow.id);
      let idParts = fullId.split('_');

      if (idParts.length < 2) {
        this.notificationsService.notifyError(
          this.intl.t('knowledge-hub.filterable-list.sources.actions.resync-error'),
        );
        return;
      }

      let contentWrapperId = idParts.slice(1).join('_');

      let folder = this.getFolder();
      if (!folder) {
        this.notificationsService.notifyError(
          this.intl.t('knowledge-hub.filterable-list.sources.actions.resync-error'),
        );
        return;
      }

      let syncSource = this.getSyncSource(folder);
      if (syncSource) {
        syncSource.status = SyncStatus.SYNCING;
      }

      let sourceId = folder.contentImportSourceId;
      if (!sourceId) {
        this.notificationsService.notifyError(
          this.intl.t('knowledge-hub.filterable-list.sources.actions.resync-error'),
        );
        return;
      }

      let contentImportSource = this.store.peekRecord(
        'content-service/content-import-source',
        sourceId,
      );

      if (!contentImportSource) {
        this.notificationsService.notifyError(
          this.intl.t('knowledge-hub.filterable-list.sources.actions.resync-error'),
        );
        return;
      }

      yield contentImportSource.triggerResync([contentWrapperId]);
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.filterable-list.sources.actions.resync-error'),
      );
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::FilterableList::Table::Cells::RowActions': typeof RowActions;
    'knowledge-hub/filterable-list/table/cells/row-actions': typeof RowActions;
  }
}
